import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ArrowLeft, ArrowRight } from "@unified-checkout/design-system/icons/index";
import { Button } from "@unified-checkout/design-system";

import type { IInnerLayout } from "@app/dashboard/types/innerLayout";
import { LOCAL_STORAGE_KEYS } from "@app/dashboard/constants/local-storage-keys";
import { MOBILE_BREAKPOINT } from "@app/dashboard/constants/break-points";
import { useTestLiveModeStore } from "@app/dashboard/store/testLiveMode";
import { useNavigationStore } from "@app/dashboard/store/navigation";
import { useLangStore } from "@app/dashboard/store/lang";
import { useScreenWidth } from "@app/dashboard/hooks/useScreenWidth";
import { useGoBack } from "@app/dashboard/hooks/useGoBack";
import { isCreationModule } from "@app/dashboard/utils/isCreationModule";
import { getFromLocalStorage } from "@app/dashboard/utils/manageLocalStorage";
import MobileNavMenu from "@app/dashboard/components/inner_layout/MobileNavMenu";
import SideMenu from "@app/dashboard/components/inner_layout/SideMenu";
import Navbar from "@app/dashboard/components/inner_layout/Navbar";
import { SubmitFeedback } from "@app/dashboard/components/inner_layout/SubmitFeedback";
import { InfoBar } from "@app/dashboard/components/inner_layout/InfoBar";
import { DashboardGuide } from "@app/dashboard/components/inner_layout/DashboardGuide";

import { initAmplitudeUserId } from "../../pages/outer_layout/Login/utils";
import { chatbotWidgetInitializer, freshchatWidgetInitializer, useInnerLayoutLogic } from "./utils";
import styles from "./inner-layout.module.scss";


const InnerLayout = ({ children }: IInnerLayout) => {
  const { t } = useTranslation();

  const { activeLink } = useNavigationStore();
  const { lang } = useLangStore();
  const { isLive } = useTestLiveModeStore();

  const screenWidth = useScreenWidth();
  const [isSideMenuCollpased, setIsSideMenuCollpased] = useState(false);

  const isMobile = screenWidth <= MOBILE_BREAKPOINT;
  const showDashboardGuide = getFromLocalStorage(LOCAL_STORAGE_KEYS.SHOW_DASHBOARD_TOUR);

  const {
    onTestToggleChange,
    editProfileHandler,
    logoutHandler,
    handleSwitchDashboard,
    isWidgetsLoaded,
    feedbackModal,
  } = useInnerLayoutLogic();

  useEffect(() => {
    const WINDOW: any = window;
    // freshworksWidgetInitializer();
    freshchatWidgetInitializer();
    chatbotWidgetInitializer();
    initAmplitudeUserId();

    // Hide freshworks widget trigger btn
    // WINDOW?.FreshworksWidget?.('hide', 'launcher');

    return () => {
      try {
        WINDOW?.fcWidget?.destroy?.();
        // WINDOW?.FreshworksWidget?.('destroy');
        WINDOW?.__paymobChatbotInstance?.destroy?.();
      } catch (error) {
        console.log('error :', error);
        // Ignore destroy errors when widget is not mounted
      }
    };
  }, []);


  const creationModule = isCreationModule();

  const goBack = useGoBack();

  return (
    creationModule ?
      <div className={styles["inner-layout--creation-module"]}>

        <nav className={styles["inner-layout--creation-module__action-bar"]}>

          <Button
            size="md"
            color="secondary"
            variant="link"
            leadingIcon={lang === 'en' ? ArrowLeft : ArrowRight}
            onClickHandler={() => goBack()}
          />

          <h1 className={styles["inner-layout--creation-module__action-bar__title"]}>
            {activeLink.sub || activeLink.nav}
          </h1>

          {/* <div className={styles["inner-layout--creation-module__action-bar__actions"]}>
            <span>ACTION1</span>
            <span>ACTION2</span>
          </div> */}

        </nav>

        {!isLive && <InfoBar status="warning" text={t('innerLayout.navbar.youAreinTestMode')} />}

        <main>
          {children}
        </main>
      </div>
      :
      <div className={styles["inner-layout"]} data-is-side-menu-collapsed={isSideMenuCollpased}>
        {
          !isMobile &&
          <SideMenu
            isSideMenuCollpased={isSideMenuCollpased}
            setIsSideMenuCollpased={setIsSideMenuCollpased}
            editProfileHandler={editProfileHandler}
            logoutHandler={logoutHandler}
          />
        }
        {
          isMobile &&
          <MobileNavMenu
            onTestToggleChange={onTestToggleChange}
            editProfileHandler={editProfileHandler}
            logoutHandler={logoutHandler}
          />
        }
        <div className={styles["inner-layout__main-wrapper"]}>
          {
            !isMobile &&
            <Navbar
              onTestToggleChange={onTestToggleChange}
              isWidgetsLoaded={isWidgetsLoaded}
            />
          }
          <main>
            {children}
          </main>
        </div>

        <SubmitFeedback feedbackModal={feedbackModal} />

        {!isMobile && showDashboardGuide && <DashboardGuide />}

      </div>
  )
}

export default InnerLayout;
